/** 
 * src/services/operationService.js
 * Operation Service
 * Handles file operations with queuing, retry logic, and progress tracking
 */
import { EventEmitter } from 'events';

// Operation type constants
export const OperationType = {
    MOVE: 'move',
    DELETE: 'delete',
    DOWNLOAD: 'download',
    TIMELAPSE: 'timelapse',
	PROFILE: 'profile',
    UPLOAD_TO_GALLERY: 'upload_to_gallery'
};

// Operation limits
export const OPERATION_LIMITS = {
    MOVE: 999999,
    DELETE: 999999,
    DOWNLOAD: 1,
    MAX_RETRIES: 3,
    RETRY_DELAY: 2000, // 2 seconds
    BATCH_DELAY: 500,   // 500ms between operations
	CHUNK_SIZE: 300,
	TIMELAPSE_BATCH_SIZE: 300,
    TIMELAPSE_DELAY: 500
};

class OperationService {
    constructor() {
        this.queue = [];
        this.processing = false;
        this.events = new EventEmitter();
        this.currentOperation = null;
    }

    /**
     * Add operation to queue
     * @param {Object} operation - Operation details
     * @param {string} operation.type - Operation type (move, delete, download)
     * @param {Array} operation.files - Files to process
     * @param {Function} operation.handler - Async function to handle each file
     * @param {Object} operation.context - Additional context data
     * @returns {string} Operation ID
     */
    addOperation({ type, files, handler, context = {} }) {
        const operationId = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        this.queue.push({
            id: operationId,
            type,
            files,
            handler,
            context,
            status: {
                completed: 0,
                failed: [],
                total: files.length,
                inProgress: false
            }
        });

        // Start processing if not already running
        if (!this.processing) {
            this.processQueue();
        }

        return operationId;
    }

    /**
     * Process operations queue
     * @private
     */
    async processQueue() {
        if (this.processing || this.queue.length === 0) {
            return;
        }

        this.processing = true;
        
        while (this.queue.length > 0) {
            this.currentOperation = this.queue[0];
            this.currentOperation.status.inProgress = true;

            try {
                await this.processOperation(this.currentOperation);
            } catch (error) {
                this.events.emit('error', {
                    operationId: this.currentOperation.id,
                    error
                });
            }

            this.queue.shift();
            this.currentOperation = null;
        }

        this.processing = false;
    }

    /**
     * Process single operation with retries
     * @private
     * @param {Object} operation - Operation to process
     */
    async processOperation(operation) {
    const { files, handler, context } = operation;
    let lastError = null;

    // Process files in chunks to avoid payload size limits
    const batches = [];
    for (let i = 0; i < files.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
        batches.push(files.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE));
    }

        let totalProcessed = 0;

        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            let retries = 0;
            let success = false;

            while (retries < OPERATION_LIMITS.MAX_RETRIES && !success) {
                try {
                    // Create progress callback for real-time updates
                    const progressCallback = (progressData) => {
                        this.events.emit('progress', {
                            operationId: operation.id,
                            type: operation.type,
                            completed: progressData.completed || totalProcessed,
                            total: files.length,
                            uploadProgress: progressData.uploadProgress,
                            currentFile: progressData.currentFile,
                            currentFileName: progressData.currentFileName,
                            batch,
                            currentBatch: i + 1,
                            totalBatches: batches.length
                        });
                    };

                    // Process the entire batch with progress callback
                    await handler(batch, context, progressCallback);
                    success = true;
                    totalProcessed += batch.length;

                    // Emit progress more frequently
                    this.events.emit('progress', {
                        operationId: operation.id,
                        type: operation.type,
                        completed: totalProcessed,
                        total: files.length,
                        batch,
                        currentBatch: i + 1,
                        totalBatches: batches.length
                    });

                    // Small delay between batches for UI responsiveness
                    if (i < batches.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }

                } catch (error) {
                    lastError = error;
                    retries++;

                    // Check for auth errors - don't retry these
                    if (error?.response?.status === 401) {
                        batch.forEach(file => {
                            operation.status.failed.push({
                                file,
                                error: 'Authentication failed',
                                permanent: true
                            });
                        });
                        break;
                    }

                    if (retries === OPERATION_LIMITS.MAX_RETRIES) {
                        batch.forEach(file => {
                            operation.status.failed.push({
                                file,
                                error: error.message
                            });
                        });
                    }

                    await new Promise(resolve => setTimeout(resolve, OPERATION_LIMITS.RETRY_DELAY));
                }
            }

            // Update final status after each batch
            operation.status.completed = totalProcessed;

            if (operation.status.failed.some(f => f.permanent)) {
                break;
            }
        }

        // Emit completion with final status
        this.events.emit('complete', {
            operationId: operation.id,
            type: operation.type,
            status: {
                ...operation.status,
                completed: totalProcessed
            },
            context: operation.context,
            error: lastError
        });
    }

    /**
     * Subscribe to operation events
     * @param {string} eventType - Event type (progress, complete, error)
     * @param {Function} handler - Event handler
     */
    on(eventType, handler) {
        this.events.on(eventType, handler);
    }

    /**
     * Unsubscribe from operation events
     * @param {string} eventType - Event type
     * @param {Function} handler - Event handler
     */
    off(eventType, handler) {
        this.events.off(eventType, handler);
    }

    /**
     * Get operation status
     * @param {string} operationId - Operation ID
     * @returns {Object|null} Operation status
     */
    getStatus(operationId) {
        const operation = this.queue.find(op => op.id === operationId);
        return operation ? { ...operation.status } : null;
    }

    /**
     * Cancel operation if possible
     * @param {string} operationId - Operation ID
     * @returns {boolean} Whether operation was cancelled
     */
    cancelOperation(operationId) {
        const index = this.queue.findIndex(op => op.id === operationId);
        if (index === -1) return false;

        // Can't cancel in-progress operations
        if (this.queue[index].status.inProgress) return false;

        this.queue.splice(index, 1);
        return true;
    }
}

// Export singleton instance
export const operationService = new OperationService();